import { del, get, post, Requestable } from "./api-components"
import { CurrencyConversion, TripExpense, TripExpenseEntry, TripSettlement } from "./types/expense"
import { Group, GroupDetails, GroupInvite, GroupInvitePreview, GroupLog } from "./types/group"
import { Trip, TripSubscription, TripUser, TripInvite, TripInvitePreview, TripLog, TripPublicPreview } from "./types/trip"
import { TripMedia } from "./types/trip-media"
import type { User, UserSetting } from "./types/user"
import type { UserAuthenticationMethods } from "./types/authentication"
import type { ChatMessage, ChatPage } from "./types/chat"
import type { AdminAnalyticsOverview, AdminUser, AdminUserPage } from "./types/analytics"
import type { AccountType } from "./account-types"

export const apiIp = process.env.NEXT_PUBLIC_API_ADDR || 'https://api.boytrips.com'
export const liveUpdatesIp = `${apiIp.replace(/^http/, 'ws').replace(/\/$/, '')}/live/updates`

type Failed = { failed: string }
type PathId = { path: { id: string } }
type IconInput = { base64: string; type: string }
type ExpenseEntryInput = { name: string; predictedName?: string; amount: number; claimedBy?: string; paid?: boolean }

export type LiveUpdateTopic = 'trip' | 'group' | 'trip.expense' | 'trip.expense.entry' | 'trip.media' | 'trip.invite' | 'group.invite' | 'group.log' | 'trip.subscription' | 'trip.log' | 'user' | 'user.setting' | 'user.authentication' | 'chat.message'
export type LiveUpdateRequest = { op: 'ping' } | { op: 'sub'; key: string; topic: LiveUpdateTopic; options?: Record<string, unknown> } | { op: 'unsub'; key: string; topic: LiveUpdateTopic; options?: Record<string, unknown> }
export type LiveUpdateResponse = { op: 'authenticated' | 'pong' } | { op: 'error'; message: string } | { op: 'create' | 'update' | 'delete'; key: string; data: unknown }

function hosted<T extends { [key: string]: any }>(host: string, obj: T): T {
    let queue: [string, any][][] = [Object.entries(obj)]
    while (queue.length > 0) {
        const level = queue.pop()!
        for (const [, v] of level) {
            if (v instanceof Requestable) {
                v.setHost(host)
            } else if (v && typeof v == 'object') {
                queue.push(Object.entries(v))
            }
        }
    }
    return obj
}

export const api = hosted(apiIp, {
    organization: {
        user: {
            authentication: {
                session: get<{ user: User }>("/user/authentication/session"),
                logout: post<{ loggedOut: true }>("/user/authentication/logout"),
                login: post<Failed | { token: string; twoStep: true; types: string[]; methods: { _id: string; type: string; name: string }[] } | { loggedIn: true } | { linkRequired: true; provider: "google"; email: string } | { providerRequired: "google"; email: string }, { body: { identity?: string; provider: { type: "password"; password: string } | { type: "google"; token: string } } }>("/user/authentication/attempt-login"),
                verify: post<Failed | { loggedIn: true }, { body: { method: string; code: string; token: string } }>("/user/authentication/verify-mfa"),
            },
            signup: {
                start: post<Failed & { conflict?: "email" | "handle" } | { created?: boolean; user?: string; loginRequired?: boolean; loggedIn?: boolean; mfaSetupRecommended?: boolean; existing?: boolean; provider?: "google" | "password"; linkRequired?: boolean; providerRequired?: "google"; email?: string }, { body: { displayName?: string; email?: string; password?: string; googleToken?: string } }>("/user/signup"),
            },
            account: {
                authentication: {
                    methods: get<{ methods: UserAuthenticationMethods }>("/user/account/authentication"),
                    google: {
                        link: post<Failed | { linked: true; methods: UserAuthenticationMethods }, { body: { token: string } }>("/user/account/authentication/google"),
                        unlink: del<Failed | { unlinked: true; methods: UserAuthenticationMethods }>("/user/account/authentication/google"),
                    },
                    password: {
                        set: post<Failed | { added?: true; updated?: true; methods: UserAuthenticationMethods }, { body: { password: string; currentPassword?: string } }>("/user/account/authentication/password"),
                        remove: post<Failed | { removed: true; methods: UserAuthenticationMethods }, { body: { currentPassword: string } }>("/user/account/authentication/password/remove"),
                    },
                },
                profile: {
                    get: get<{ user: User; settings: UserSetting }>("/user/account/profile"),
                    update: post<Failed | { user: User }, { body: { displayName?: string; handle?: string | null; avatar?: IconInput | null } }>("/user/account/profile"),
                },
                settings: {
                    get: get<{ settings: UserSetting }>("/user/account/settings"),
                    update: post<Failed | { settings: UserSetting }, { body: { currency?: string; links?: { venmo?: string | null } } }>("/user/account/settings"),
                },
                mfa: {
                    status: get<{ configured: boolean; required: boolean; recommended?: boolean }>("/user/account/mfa/status"),
                    totp: {
                        start: post<Failed | { method?: string; type?: string; issuer?: string; label?: string; secret?: string; uri?: string }, { body: { name?: string } }>("/user/account/mfa/totp/start"),
                        verify: post<Failed | { loggedIn?: boolean }, { body: { method?: string; code?: string } }>("/user/account/mfa/totp/verify"),
                    },
                }
            }
        },


        analytics: {
            session: post<{ tracked: true }, { body: { sessionId: string; visitorId: string; path: string; pageView?: boolean; title?: string; referrer?: string; timezone?: string; language?: string; utm?: { source?: string; medium?: string; campaign?: string }; screen?: { width?: number; height?: number; pixelRatio?: number } } }>("/analytics/session", {}, { logFailure: false, stopFailure: true, nonOk: { tracked: true } }),
        },

        admin: {
            analytics: {
                overview: get<Failed | AdminAnalyticsOverview, { query: { days: string } }>("/admin/analytics"),
            },
            user: {
                list: get<Failed | AdminUserPage, { query: { limit: string; after?: string; search?: string; accountType?: string } }>("/admin/user"),
                updateAccountType: post<Failed | { user: AdminUser }, { path: { id: string }; body: { accountType: AccountType } }>("/admin/user/:id"),
            },
        },

        currency: {
            convert: get<Failed | CurrencyConversion, { query: { from: string; to: string; amount: string } }>("/currency/convert"),
        },

        chat: {
            list: get<Failed | ChatPage, { path: { type: 'trip' | 'group'; id: string }; query?: { before?: string; limit?: string } }>("/chat/:type/:id"),
            send: post<Failed | { created: true; message: ChatMessage }, { path: { type: 'trip' | 'group'; id: string }; body: { message: string } }>("/chat/:type/:id"),
            update: post<Failed | { message: ChatMessage }, { path: { type: 'trip' | 'group'; id: string; messageId: string }; body: { message: string } }>("/chat/:type/:id/:messageId"),
            delete: del<Failed | { deleted: true }, { path: { type: 'trip' | 'group'; id: string; messageId: string } }>("/chat/:type/:id/:messageId"),
        },

        trip: {
            public: { preview: get<Failed | { trip: TripPublicPreview }, { path: { handle: string } }>("/trip/public/:handle") },
            list: get<Failed | { trips: Trip[] }, { query?: { owner?: 'user' | 'group' | 'all' } }>("/trip"),
            create: post<Failed | { created: true; trip: Trip }, { body: { name: string; group?: string } }>("/trip/create"),
            get: get<Failed | { trip: Trip; subscriptions: TripSubscription[]; users: TripUser[] }, PathId>("/trip/:id"),
            update: post<Failed | { trip: Trip }, PathId & { body: { dates?: { from?: string | Date; to?: string | Date } | null; name?: string; icon?: IconInput | null; description?: string | null } }>("/trip/:id"),
            delete: del<Failed | { deleted: true }, PathId>("/trip/:id"),

            member: {
                remove: del<Failed | { removed: true }, { path: { id: string; userId: string } }>("/trip/:id/member/:userId"),
            },

            invite: {
                list: get<Failed | { invites: TripInvite[] }, PathId>("/trip/:id/invite"),
                create: post<Failed | { created: true; invite: TripInvite }, PathId & { body: { expires?: string | Date } }>("/trip/:id/invite"),
                update: post<Failed | { invite: TripInvite }, { path: { id: string; inviteId: string }; body: { expires: string | Date } }>("/trip/:id/invite/:inviteId"),
                delete: del<Failed | { deleted: true }, { path: { id: string; inviteId: string } }>("/trip/:id/invite/:inviteId"),
                preview: get<Failed | { invite: TripInvitePreview }, { path: { key: string } }>("/trip/invite/:key"),
                accept: post<Failed | { accepted: true; trip: Trip }, { path: { key: string } }>("/trip/invite/:key/accept"),
            },

            log: {
                list: get<Failed | { logs: TripLog[] }, PathId & { query?: { limit?: string } }>("/trip/:id/log"),
            },

            expense: {
                list: get<Failed | { expenses: TripExpense[]; entries: TripExpenseEntry[] }, PathId>("/trip/:id/expense"),
                create: post<Failed | { created: true; expense: TripExpense; entries: TripExpenseEntry[] }, PathId & { body: { name: string; type?: 'custom' | 'receipt'; currency?: string; paidBy?: string; images?: string[]; entries?: ExpenseEntryInput[] } }>("/trip/:id/expense"),
                get: get<Failed | { expense: TripExpense; entries: TripExpenseEntry[] }, { path: { id: string; expenseId: string } }>("/trip/:id/expense/:expenseId"),
                update: post<Failed | { expense: TripExpense }, { path: { id: string; expenseId: string }; body: { name?: string; type?: 'custom' | 'receipt'; currency?: string; paidBy?: string | null; images?: string[] } }>("/trip/:id/expense/:expenseId"),
                delete: del<Failed | { deleted: true }, { path: { id: string; expenseId: string } }>("/trip/:id/expense/:expenseId"),

                receipt: {
                    analyze: post<Failed | { created: true; media: TripMedia; expense: TripExpense; entries: TripExpenseEntry[] }, PathId & { body: FormData }>("/trip/:id/expense/receipt", { bodyType: 'form' }),
                },
                settlement: {
                    get: get<Failed | TripSettlement, PathId>("/trip/:id/expense/settlement"),
                    markPaid: post<Failed | { paid: true; balances: { currency: string; amount: number; count: number }[]; count: number }, { path: { id: string; payeeId: string }; body: { currency?: string; currencies?: string[] } }>("/trip/:id/expense/settlement/:payeeId/paid"),
                },
                entry: {
                    create: post<Failed | { created: true; entry: TripExpenseEntry }, { path: { id: string; expenseId: string }; body: ExpenseEntryInput }>("/trip/:id/expense/:expenseId/entry"),
                    update: post<Failed | { entry: TripExpenseEntry }, { path: { id: string; expenseId: string; entryId: string }; body: { name?: string; predictedName?: string; amount?: number; claimedBy?: string | null; paid?: boolean } }>("/trip/:id/expense/:expenseId/entry/:entryId"),
                    splitEvenly: post<Failed | { split: true; count: number; entries: TripExpenseEntry[] }, { path: { id: string; expenseId: string; entryId: string } }>("/trip/:id/expense/:expenseId/entry/:entryId/split"),
                    merge: post<Failed | { merged: true; deletedIds: string[]; entry: TripExpenseEntry }, { path: { id: string; expenseId: string }; body: { entryIds: string[] } }>("/trip/:id/expense/:expenseId/entry/merge"),
                    delete: del<Failed | { deleted: true }, { path: { id: string; expenseId: string; entryId: string } }>("/trip/:id/expense/:expenseId/entry/:entryId"),
                },
            },

            media: {
                list: get<Failed | { media: TripMedia[] }, PathId & { query?: { purpose?: 'gallery' | 'receipt' | 'all'; parent?: string; type?: 'file' | 'folder' | 'all' } }>("/trip/:id/media"),
                upload: post<Failed | { created: true; media: TripMedia }, PathId & { body: FormData }>("/trip/:id/media", { bodyType: 'form' }),
                resumable: {
                    start: post<Failed | { created: true; media: TripMedia; uploadUrl: string }, PathId & { body: { name: string; mime: string; size: number; width: number; height: number; purpose?: 'gallery' | 'receipt'; parent?: string | null; caption?: string; takenAt?: string | Date } }>("/trip/:id/media/upload/start"),
                    complete: post<Failed | { completed?: true; processing?: true; media: TripMedia }, { path: { id: string; mediaId: string } }>("/trip/:id/media/:mediaId/upload/complete"),
                },
                folder: {
                    create: post<Failed | { created: true; media: TripMedia }, PathId & { body: { name: string; parent?: string | null; purpose?: 'gallery' | 'receipt' } }>("/trip/:id/media/folder"),
                },
                item: {
                    get: get<Failed | { media: TripMedia; children?: TripMedia[] }, { path: { id: string; mediaId: string } }>("/trip/:id/media/:mediaId"),
                    update: post<Failed | { media: TripMedia }, { path: { id: string; mediaId: string }; body: { name?: string; caption?: string | null; takenAt?: string | Date | null; parent?: string | null } }>("/trip/:id/media/:mediaId"),
                    delete: del<Failed | { deleted: true; count: number }, { path: { id: string; mediaId: string } }>("/trip/:id/media/:mediaId"),
                },
            },
        },

        group: {
            list: get<Failed | { groups: Group[] }>("/group"),
            create: post<Failed | { created: true; group: Group }, { body: { name: string } }>("/group/create"),
            get: get<Failed | GroupDetails, PathId>("/group/:id"),
            update: post<Failed | { group: Group }, PathId & { body: { name?: string; icon?: IconInput | null } }>("/group/:id"),
            delete: del<Failed | { deleted: true }, PathId>("/group/:id"),

            member: {
                update: post<Failed | { group: Group }, { path: { id: string; userId: string }; body: { role: 'admin' | 'member' } }>("/group/:id/member/:userId"),
                remove: del<Failed | { removed: true }, { path: { id: string; userId: string } }>("/group/:id/member/:userId"),
            },

            invite: {
                list: get<Failed | { invites: GroupInvite[] }, PathId>("/group/:id/invite"),
                create: post<Failed | { created: true; invite: GroupInvite }, PathId & { body: { expires?: string | Date } }>("/group/:id/invite"),
                update: post<Failed | { invite: GroupInvite }, { path: { id: string; inviteId: string }; body: { expires: string | Date } }>("/group/:id/invite/:inviteId"),
                delete: del<Failed | { deleted: true }, { path: { id: string; inviteId: string } }>("/group/:id/invite/:inviteId"),
                preview: get<Failed | { invite: GroupInvitePreview }, { path: { key: string } }>("/group/invite/:key"),
                accept: post<Failed | { accepted: true; group: Group }, { path: { key: string } }>("/group/invite/:key/accept"),
            },

            log: {
                list: get<Failed | { logs: GroupLog[] }, PathId & { query?: { limit?: string } }>("/group/:id/log"),
            },
        },

        live: {
            updates: liveUpdatesIp,
        },
    }
})

function reciprocateApiHosts(fur: string | undefined, host: string) {
    const obj = fur == undefined ? api : api[fur as keyof typeof api]
    if (obj == undefined) return

    let queue: [string, any][][] = [Object.entries(obj)]
    while (queue.length > 0) {
        const level = queue.pop()!
        for (const [, v] of level) {
            if (v instanceof Requestable && !v.getHost()) {
                v.setHost(host)
            } else if (v && typeof v == 'object') {
                queue.push(Object.entries(v))
            }
        }
    }
}

reciprocateApiHosts(undefined, apiIp)